import type { Corners, Point } from '@/tools/image-engine';

/** On-screen size of a handle's target (WCAG 2.5.8 and the #387 boards: 44 px). */
export const HANDLE_CSS_PX = 44;
/** The loupe: its on-screen size and how much it magnifies. */
export const LOUPE_CSS_PX = 96;
const LOUPE_ZOOM = 3;

export function replaceCorner(corners: Corners, index: number, point: Point): Corners {
  return [
    index === 0 ? point : corners[0],
    index === 1 ? point : corners[1],
    index === 2 ? point : corners[2],
    index === 3 ? point : corners[3],
  ];
}

/** Source pixels per CSS pixel: handles and steps are sized on screen, not in the photo. */
export function canvasScale(canvas: HTMLCanvasElement | null): number {
  if (!canvas) return 1;
  const width = canvas.getBoundingClientRect().width;
  return width > 0 ? canvas.width / width : 1;
}

/** A pointer position as a point in the photo, kept inside it. */
export function toSourcePoint(canvas: HTMLCanvasElement, clientX: number, clientY: number): Point {
  const rect = canvas.getBoundingClientRect();
  const x = ((clientX - rect.left) * canvas.width) / (rect.width || 1);
  const y = ((clientY - rect.top) * canvas.height) / (rect.height || 1);
  return {
    x: Math.max(0, Math.min(canvas.width, x)),
    y: Math.max(0, Math.min(canvas.height, y)),
  };
}

/** The corner within reach of `p` (a handle's radius and a half on screen), or -1. */
export function nearestCorner(corners: Corners, p: Point, scale: number): number {
  let best = -1;
  let bestDistance = Infinity;
  corners.forEach((c, i) => {
    const d = Math.hypot(c.x - p.x, c.y - p.y);
    if (d < bestDistance) {
      bestDistance = d;
      best = i;
    }
  });
  return bestDistance <= (HANDLE_CSS_PX / 2) * scale * 1.5 ? best : -1;
}

const DELTAS: Readonly<Record<string, readonly [number, number] | undefined>> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
};

/** The corner an arrow key moves to, or null when the key is not an arrow. */
export function arrowMove(
  corner: Point,
  key: string,
  shift: boolean,
  size: { readonly width: number; readonly height: number },
): Point | null {
  const delta = DELTAS[key];
  if (!delta) return null;
  const step = Math.max(1, size.width * (shift ? 0.01 : 0.0025));
  return {
    x: Math.max(0, Math.min(size.width, corner.x + delta[0] * step)),
    y: Math.max(0, Math.min(size.height, corner.y + delta[1] * step)),
  };
}

/** The photo, a scrim outside the sheet, the sheet's outline and a handle on each corner. */
export function drawCorners(
  canvas: HTMLCanvasElement,
  source: HTMLCanvasElement,
  c: Corners,
  selected: number,
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  const s = canvasScale(canvas);
  ctx.drawImage(source, 0, 0);
  const accent = getComputedStyle(canvas).getPropertyValue('--ring').trim() || '#2563eb';
  ctx.save();
  // A scrim outside the sheet; an off-DOM-style overlay with no design-system token, as in the
  // identity-documents rectifier (SL-0027, Resolution 9).
  ctx.fillStyle = 'rgba(19,26,34,0.45)';
  ctx.beginPath();
  ctx.rect(0, 0, canvas.width, canvas.height);
  ctx.moveTo(c[0].x, c[0].y);
  for (let i = 3; i >= 0; i--) ctx.lineTo(c[i]!.x, c[i]!.y);
  ctx.closePath();
  ctx.fill('evenodd');
  ctx.restore();
  ctx.strokeStyle = accent;
  ctx.lineWidth = 2.5 * s;
  ctx.beginPath();
  ctx.moveTo(c[0].x, c[0].y);
  for (let i = 1; i < 4; i++) ctx.lineTo(c[i]!.x, c[i]!.y);
  ctx.closePath();
  ctx.stroke();
  c.forEach((p, i) => {
    ctx.beginPath();
    ctx.arc(p.x, p.y, (i === selected ? 14 : 11) * s, 0, Math.PI * 2);
    ctx.fillStyle = '#ffffff';
    ctx.fill();
    ctx.lineWidth = 3 * s;
    ctx.stroke();
  });
}

/** The loupe: the point under the finger magnified, with a cross at its centre. */
export function drawLoupe(
  loupeCanvas: HTMLCanvasElement,
  source: HTMLCanvasElement,
  corner: Point,
  scale: number,
): void {
  const ctx = loupeCanvas.getContext('2d');
  if (!ctx) return;
  const size = loupeCanvas.width;
  const span = (size / LOUPE_ZOOM) * scale * (LOUPE_CSS_PX / size);
  ctx.clearRect(0, 0, size, size);
  ctx.drawImage(source, corner.x - span / 2, corner.y - span / 2, span, span, 0, 0, size, size);
  const accent = getComputedStyle(loupeCanvas).getPropertyValue('--ring').trim() || '#2563eb';
  ctx.strokeStyle = accent;
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(size / 2, size / 2 - 12);
  ctx.lineTo(size / 2, size / 2 + 12);
  ctx.moveTo(size / 2 - 12, size / 2);
  ctx.lineTo(size / 2 + 12, size / 2);
  ctx.stroke();
}
